import React, {useState} from 'react';
import {Button, Input, InputNumber, Popconfirm, Table, message} from 'antd';
import {useMutation} from '@apollo/client';
import type {ColumnsType} from 'antd/es/table';
import TablePaginationBar from '../../../../components/TablePaginationBar';
import {useBcpCheckUserGroupListQuery} from './hooks/useBcpCheckUserGroupListQuery';
import {DELETE_BCP_CHECK_USER_GROUP} from './graphql/mutations/bcpCheckUserGroupMutations';
import {CheckUserGroupType} from './graphql/types/BcpCheckUserGroupTypes';
import BcpCheckUserGroupDetailModal from './components/BcpCheckUserGroupDetailModal';
import BcpCheckUserGroupFormModal from './components/BcpCheckUserGroupFormModal';
import {formatTime} from './utils/timeFormat';

interface CheckUserGroupFilterType {
    id?: number;
    groupName?: string;
}

const initialFilters: CheckUserGroupFilterType = {};

const BcpCheckUserGroupListPage: React.FC = () => {
    const [tempFilters, setTempFilters] = useState<CheckUserGroupFilterType>(initialFilters);
    const [filters, setFilters] = useState<CheckUserGroupFilterType>(initialFilters);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [shouldFetch, setShouldFetch] = useState(true);
    const [detailVisible, setDetailVisible] = useState(false);
    const [selectedGroupId, setSelectedGroupId] = useState<number | undefined>(undefined);
    const [editGroupId, setEditGroupId] = useState<number | undefined>(undefined);
    const [formModalVisible, setFormModalVisible] = useState(false);

    const {data, loading, refetch, totalElements} = useBcpCheckUserGroupListQuery(
        filters,
        currentPage,
        pageSize,
        shouldFetch,
    );

    const [deleteGroup] = useMutation(DELETE_BCP_CHECK_USER_GROUP);

    const handleViewDetail = (record: CheckUserGroupType) => {
        setSelectedGroupId(record.id);
        setDetailVisible(true);
    };

    const handleEdit = (record: CheckUserGroupType) => {
        setEditGroupId(record.id);
        setFormModalVisible(true);
    };

    const handleDelete = async (id: number) => {
        try {
            await deleteGroup({variables: {id}});
            message.success('删除成功');
            refetch();
        } catch (e: any) {
            message.error(e?.message || '删除失败');
        }
    };

    const columns: ColumnsType<CheckUserGroupType> = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 80,
        },
        {
            title: '用户组名',
            dataIndex: 'groupName',
            key: 'groupName',
            width: 200,
        },
        {
            title: '备注',
            dataIndex: 'remark',
            key: 'remark',
            width: 200,
        },
        {
            title: '创建人',
            dataIndex: 'createUser',
            key: 'createUser',
            width: 120,
        },
        {
            title: '创建时间',
            dataIndex: 'createTime',
            key: 'createTime',
            width: 180,
            render: (t: string) => formatTime(t),
        },
        {
            title: '操作',
            key: 'action',
            width: 250,
            render: (_: unknown, record: CheckUserGroupType) => (
                <div className="flex gap-2">
                    <Button type="link" size="small" onClick={() => handleViewDetail(record)}>详情</Button>
                    <Button type="link" size="small" onClick={() => handleEdit(record)}>编辑</Button>
                    <Popconfirm
                        title="确认删除该用户组？"
                        onConfirm={() => handleDelete(record.id!)}
                        okText="确认"
                        cancelText="取消"
                    >
                        <Button type="link" size="small" danger>删除</Button>
                    </Popconfirm>
                </div>
            ),
        },
    ];

    const handleSearch = () => {
        setFilters({...tempFilters});
        setCurrentPage(1);
        setShouldFetch(true);
    };

    const handleReset = () => {
        setTempFilters(initialFilters);
        setFilters(initialFilters);
        setCurrentPage(1);
        setShouldFetch(true);
    };

    const handlePageChange = (page: number, size: number) => {
        setCurrentPage(page);
        setPageSize(size);
    };

    const handleCreate = () => {
        setEditGroupId(undefined);
        setFormModalVisible(true);
    };

    const handleFormSuccess = () => {
        setFormModalVisible(false);
        setEditGroupId(undefined);
        refetch();
    };

    return (
        <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-3">
            <h2 className="text-base font-semibold mb-4">用户组管理</h2>
            <div className="grid grid-cols-3 gap-x-4 gap-y-3 mb-4">
                <div className="flex items-center">
                    <span className="w-[5em] flex-shrink-0 text-right pr-2">用户组ID</span>
                    <InputNumber
                        className="w-full"
                        value={tempFilters.id}
                        onChange={(v) => setTempFilters({...tempFilters, id: v ?? undefined})}
                        placeholder="请输入用户组ID"
                        min={0}
                    />
                </div>
                <div className="flex items-center">
                    <span className="w-[5em] flex-shrink-0 text-right pr-2">用户组名</span>
                    <Input
                        className="w-full"
                        value={tempFilters.groupName || ''}
                        onChange={(e) => setTempFilters({...tempFilters, groupName: e.target.value || undefined})}
                        placeholder="请输入用户组名"
                        allowClear
                    />
                </div>
            </div>
            <div className="flex gap-2 mb-4">
                <Button type="primary" onClick={handleSearch}>查询</Button>
                <Button onClick={handleReset}>重置</Button>
                <Button type="primary" onClick={handleCreate}>新建</Button>
            </div>
            <Table
                dataSource={data}
                columns={columns}
                rowKey="id"
                loading={loading}
                pagination={false}
                scroll={{x: 1000}}
            />
            <TablePaginationBar
                total={totalElements}
                current={currentPage}
                pageSize={pageSize}
                onChange={handlePageChange}
            />
            <BcpCheckUserGroupDetailModal
                visible={detailVisible}
                groupId={selectedGroupId}
                onClose={() => setDetailVisible(false)}
            />
            <BcpCheckUserGroupFormModal
                visible={formModalVisible}
                editGroupId={editGroupId}
                onSuccess={handleFormSuccess}
                onCancel={() => {
                    setFormModalVisible(false);
                    setEditGroupId(undefined);
                }}
            />
        </div>
    );
};

export default BcpCheckUserGroupListPage;
